import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Sentry from '../utils/Sentry/Sentry';

export interface UseInvokeWithDelayProps {
  callBack: () => Promise<void>;
}

/**
 * Invokes `callBack` immediately and reports `loading` while it is pending.
 *
 * This used to wait DELAY_TO_BUMP (500ms) before firing, so that a second tap
 * within that window could cancel the bump. The delay made every bump feel
 * sluggish, and the cancel only ever worked inside that window — once the
 * request was in flight a second tap just hid the loader while the bump
 * completed anyway. Both are gone: a tap bumps straight away, and further
 * taps are ignored until the request settles.
 */
export function useInvokeWithDelay({ callBack }: UseInvokeWithDelayProps) {
  const isInvoking = useRef(false);
  const [showLoader, setShowLoader] = useState(false);

  // Callers pass a fresh closure on every render. Read it through a ref so
  // `invokeFunction` keeps a stable identity — DocketItemHeader lists it in
  // the dependencies of its auto-retry effect, which would otherwise re-run
  // on every render rather than only when the bump settles.
  const savedCallBack = useRef(callBack);
  useLayoutEffect(() => {
    savedCallBack.current = callBack;
  }, [callBack]);

  const invokeFunction = useCallback(async () => {
    if (isInvoking.current) return;
    isInvoking.current = true;
    setShowLoader(true);
    try {
      const invoke = savedCallBack.current;
      invoke && (await invoke());
    } catch (error) {
      // Observe the rejection rather than leaving it unhandled: invokeFunction
      // is fired from onPress, so nothing downstream awaits its result. A
      // failed bump is otherwise silent to the user, so report it.
      console.error('Failed to invoke bump', error);
      Sentry.captureException(error);
    } finally {
      isInvoking.current = false;
      setShowLoader(false);
    }
  }, []);

  return useMemo(
    () => ({
      loading: showLoader,
      invokeFunction: invokeFunction,
    }),
    [invokeFunction, showLoader],
  );
}
